import Link from "next/link"

import { ENTITY_ICONS } from "@/constants/entity-icons"
import { SOFIA_DISPLAY_NAME } from "@/constants/sofia"
import { Badge, Button, Card, CardContent, cn } from "@coval/ui"

import { openCoviWithPrompt } from "@/lib/coviEvents"

import type { HomeConfigurationCounts } from "../types"
import type { HomeActivationState } from "../utils/homeActivationState"
import { buildHomeConfigurationCards } from "../utils/homeConfigurationCards"
import { HOME_CONTENT_COLUMN } from "../utils/homeContentColumn"
import {
  HOME_SOFIA_CONNECT_AGENT_SUGGESTED_PROMPTS,
  HOME_SOFIA_CONNECTED_AGENT_SUGGESTED_PROMPTS,
  HOME_SOFIA_ONBOARDING_SUGGESTED_PROMPTS,
} from "../utils/homeSofiaPrompts"
import { HomeSofiaHero } from "./HomeSofiaHero"

const SETUP_STEP_DETAILS = {
  agents: {
    description: "Connect the voice or chat agent you want to evaluate.",
    icon: ENTITY_ICONS.agents,
    readyPrompt:
      "Help me validate my connected agent with a one-turn smoke test",
    readySofiaLabel: "Test with Sofia",
    setupPrompt: "Help me connect a voice or chat agent",
    setupSofiaLabel: "Connect with Sofia",
  },
  personas: {
    description: "Define realistic users, behaviors, and edge cases.",
    icon: ENTITY_ICONS.personas,
    readyPrompt: "Help me review and improve my existing personas",
    readySofiaLabel: "Review with Sofia",
    setupPrompt:
      "Help me design a persona for the people my agent is expected to serve",
    setupSofiaLabel: "Design with Sofia",
  },
  "test-sets": {
    description: "Group the scenarios that matter most to your team.",
    icon: ENTITY_ICONS["test-sets"],
    readyPrompt: "Help me expand my test set into stronger regression coverage",
    readySofiaLabel: "Expand with Sofia",
    setupPrompt: "Help me build a small first test set for my agent",
    setupSofiaLabel: "Draft with Sofia",
  },
  metrics: {
    description: "Describe what good performance looks like.",
    icon: ENTITY_ICONS.metrics,
    readyPrompt: "Help me review and improve my existing metrics",
    readySofiaLabel: "Review with Sofia",
    setupPrompt: "Help me create metrics from my agent's expected behaviors",
    setupSofiaLabel: "Define with Sofia",
  },
} as const

const getSuggestedPrompts = (activation: HomeActivationState) => {
  if (activation.stage === "connect-agent") {
    return HOME_SOFIA_CONNECT_AGENT_SUGGESTED_PROMPTS
  }
  if (activation.hasConnectedAgent) {
    return HOME_SOFIA_CONNECTED_AGENT_SUGGESTED_PROMPTS
  }
  return HOME_SOFIA_ONBOARDING_SUGGESTED_PROMPTS
}

interface HomeEmptyOrgLandingProps {
  activation: HomeActivationState
  basePath: string
  configurationCounts: HomeConfigurationCounts
}

export function HomeEmptyOrgLanding({
  activation,
  basePath,
  configurationCounts,
}: HomeEmptyOrgLandingProps) {
  const suggestedPrompts = getSuggestedPrompts(activation)
  const configurationCards = buildHomeConfigurationCards(
    configurationCounts,
    basePath,
  )

  return (
    <div
      className="flex-1 overflow-y-auto bg-surface-contrast px-4 py-8 sm:px-6"
      data-testid="home-page-empty"
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <HomeSofiaHero variant="onboarding" suggestions={suggestedPrompts} />

        <section
          aria-labelledby="home-setup-title"
          className={cn(HOME_CONTENT_COLUMN, "space-y-3 pt-4")}
        >
          <div>
            <h2
              id="home-setup-title"
              className="text-base font-semibold text-coval-text"
            >
              Build your evaluation foundation
            </h2>
            <p className="text-sm text-coval-text-muted">
              Set up directly, or ask {SOFIA_DISPLAY_NAME} to guide any step.
              You can switch paths at any time.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {configurationCards.map((card) => {
              const details = SETUP_STEP_DETAILS[card.id]
              const Icon = details.icon
              const configured = card.count > 0
              const sofiaLabel = configured
                ? details.readySofiaLabel
                : details.setupSofiaLabel
              const sofiaPrompt = configured
                ? details.readyPrompt
                : details.setupPrompt

              return (
                <Card key={card.id} className="shadow-none">
                  <CardContent className="flex h-full flex-col gap-4 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <Icon
                        className="size-6 shrink-0 text-coval-text-muted"
                        aria-hidden
                      />
                      {configured ? (
                        <Badge variant="success">Ready</Badge>
                      ) : null}
                    </div>
                    <div className="flex-1 space-y-1">
                      <h3 className="font-medium text-coval-text">
                        {card.title}
                      </h3>
                      <p className="text-sm leading-5 text-coval-text-muted">
                        {details.description}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Button
                        asChild
                        variant={configured ? "ghost" : "secondary"}
                      >
                        <Link
                          href={configured ? card.href : card.newButtonHref}
                        >
                          {configured
                            ? `View ${card.title}`
                            : card.emptyCtaLabel}
                        </Link>
                      </Button>
                      <Button
                        variant="secondary"
                        aria-label={`${sofiaLabel} for ${card.title}`}
                        onClick={() => {
                          openCoviWithPrompt(sofiaPrompt, { surface: "home" })
                        }}
                      >
                        {sofiaLabel}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </section>
      </div>
    </div>
  )
}
